import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';

const Container = styled.div`
  max-width: 500px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const QRContainer = styled.div`
  text-align: center;
  margin: 1rem 0;
`;

const QRCode = styled.img`
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: white;
  padding: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;

  &::placeholder {
    color: ${props => props.theme.textMuted};
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimaryHover};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const BackupCodes = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  padding: 1rem;
  margin: 1rem 0;
`;

const CodeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  font-family: monospace;
  font-size: 0.9rem;
`;

const Code = styled.div`
  background: ${props => props.theme.cardBackground};
  padding: 0.5rem;
  border-radius: 4px;
  text-align: center;
  border: 1px solid ${props => props.theme.buttonPrimary};
`;

const TwoFactorSetup = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState('setup'); // 'setup', 'verify', 'backup'
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setup2FA();
  }, []);

  const setup2FA = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.SETUP_2FA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSecret(data.secret);
        setQrCode(data.qrCode);
      } else {
        setError(data.error || 'Failed to setup 2FA');
      }
    } catch (error) {
      console.error('2FA setup error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.VERIFY_2FA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBackupCodes(data.backupCodes);
        setStep('backup');
      } else {
        setError(data.error || 'Invalid token');
      }
    } catch (error) {
      console.error('2FA verification error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setToken(value);
    }
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (step === 'setup') {
    return (
      <Container>
        <h2 style={{ color: '#ffffff', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Setup Two-Factor Authentication
        </h2>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {qrCode && (
          <QRContainer>
            <QRCode src={qrCode} alt="2FA QR Code" />
          </QRContainer>
        )}

        <p style={{ textAlign: 'center', color: '#b9bbbe', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Or manually enter this code: <strong>{secret}</strong>
        </p>

        <ElevatedButton
          onClick={() => setStep('verify')}
          style={{ width: '100%', marginTop: '1rem' }}
          disabled={loading}
        >
          I've Scanned the Code
        </ElevatedButton>

        <ElevatedButton
          onClick={onCancel}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            border: '2px solid #ff4757'
          }}
        >
          Cancel
        </ElevatedButton>
      </Container>
    );
  }

  if (step === 'verify') {
    return (
      <Container>
        <h2 style={{ color: '#ffffff', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Verify Your Code
        </h2>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          Enter the 6-digit code from your authenticator app
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={verifyToken}>
          <Input
            type="text"
            placeholder="000000"
            value={token}
            onChange={handleTokenChange}
            required
            disabled={loading}
            autoFocus
          />

          <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading || token.length !== 6}>
            {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
          </ElevatedButton>
        </form>

        <ElevatedButton
          onClick={() => setStep('setup')}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            border: '2px solid #ff4757'
          }}
        >
          Back
        </ElevatedButton>
      </Container>
    );
  }

  if (step === 'backup') {
    return (
      <Container>
        <h2 style={{ color: '#ffffff', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Save Your Backup Codes
        </h2>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          ⚠️ Save these backup codes in a safe place. You can use them to access your account if you lose your device.
        </p>

        <BackupCodes>
          <CodeGrid>
            {backupCodes.map((code, index) => (
              <Code key={index}>{code}</Code>
            ))}
          </CodeGrid>
        </BackupCodes>

        <ElevatedButton
          onClick={downloadBackupCodes}
          style={{ width: '100%', marginBottom: '1rem' }}
        >
          Download Backup Codes
        </ElevatedButton>

        <ElevatedButton
          onClick={onComplete}
          style={{ width: '100%' }}
        >
          I've Saved My Backup Codes
        </ElevatedButton>
      </Container>
    );
  }

  return null;
};

export default TwoFactorSetup;